/**
 * Acceso sin contraseña (spec 022 §8, §13.1): código por correo y Google.
 *
 * `solicitarCodigo` va por el rewrite de `/api/*`; `verificarCodigo` por el
 * route handler `app/api/auth/code/verify`, que copia la cookie de sesión.
 * Google es una navegación completa, no un fetch: aquí solo se construye la URL.
 */

const API_URL =
  typeof window === "undefined"
    ? (process.env.BACKEND_INTERNAL_URL ?? "http://localhost:8000")
    : "";

export type CodigoErrorCode =
  | "CODIGO_ACCESO_INVALIDO"
  | "CODIGO_ACCESO_CADUCADO"
  | "CODIGO_ACCESO_AGOTADO"
  | "TWO_FACTOR_REQUIRED"
  | "ACCOUNT_SUSPENDED"
  // Feature 026, 2.ª tanda: dominio temporal o desechable (mailinator, yopmail…).
  | "EMAIL_DISPOSABLE"
  // Alta restringida desde Admin → Ajustes: el correo no está en la lista.
  | "SIGNUP_RESTRICTED"
  // Invitación de alta por enlace (030-lite).
  | "SIGNUP_INVITATION_INVALID"
  | "SIGNUP_INVITATION_EMAIL_MISMATCH"
  | "RATE_LIMITED"
  | "UNKNOWN";

const CODIGOS_CONOCIDOS: ReadonlySet<string> = new Set<CodigoErrorCode>([
  "CODIGO_ACCESO_INVALIDO",
  "CODIGO_ACCESO_CADUCADO",
  "CODIGO_ACCESO_AGOTADO",
  "TWO_FACTOR_REQUIRED",
  "ACCOUNT_SUSPENDED",
  "EMAIL_DISPOSABLE",
  "SIGNUP_RESTRICTED",
  "SIGNUP_INVITATION_INVALID",
  "SIGNUP_INVITATION_EMAIL_MISMATCH",
]);

export class AccesoApiError extends Error {
  readonly status: number;
  readonly code: CodigoErrorCode;

  constructor(message: string, status: number, code: CodigoErrorCode) {
    super(message);
    this.name = "AccesoApiError";
    this.status = status;
    this.code = code;
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    method: "POST",
    credentials: "include",
    cache: "no-store",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as {
      error?: { message?: string; error_code?: string | null };
      detail?: string | { msg?: string }[];
    };
    const detalle = Array.isArray(data.detail) ? data.detail[0]?.msg : data.detail;
    const errorCode = data.error?.error_code ?? "";
    const code: CodigoErrorCode =
      res.status === 429
        ? "RATE_LIMITED"
        : CODIGOS_CONOCIDOS.has(errorCode)
          ? (errorCode as CodigoErrorCode)
          : "UNKNOWN";
    throw new AccesoApiError(
      data.error?.message ?? detalle ?? `Request failed: ${res.status}`,
      res.status,
      code
    );
  }
  return (await res.json()) as T;
}

/** Pide un código de acceso. El backend responde 200 siempre salvo 429. */
export async function solicitarCodigo(
  email: string,
  idioma?: "es" | "en" | "de",
  /** El token de `/start?invite=` (030-lite): permite el alta aunque esté cerrada. */
  invitacion?: string | null
): Promise<void> {
  await post<{ enviado: boolean }>("/api/auth/code", {
    email,
    ...(idioma ? { idioma } : {}),
    ...(invitacion ? { invitacion } : {}),
  });
}

export interface CodigoVerificado {
  id: string;
  email: string;
  status: string;
  must_reset_password: boolean;
  nuevo: boolean;
  org_id: string | null;
}

/** Verifica el código; con 2FA se repite con el mismo `codigo` y `twoFactorCode` o `backupCode`. */
export async function verificarCodigo(i: {
  email: string;
  codigo: string;
  idioma?: "es" | "en" | "de";
  twoFactorCode?: string;
  backupCode?: string;
  /** País de la pantalla de acceso (§21.4); se guarda solo si se crea el local. */
  pais?: string | null;
  /** El token de `/start?invite=` (030-lite): el local nace con su ficha. */
  invitacion?: string | null;
}): Promise<CodigoVerificado> {
  return post<CodigoVerificado>("/api/auth/code/verify", {
    email: i.email,
    codigo: i.codigo,
    ...(i.idioma ? { idioma: i.idioma } : {}),
    ...(i.twoFactorCode ? { two_factor_code: i.twoFactorCode } : {}),
    ...(i.backupCode ? { backup_code: i.backupCode } : {}),
    ...(i.pais ? { pais: i.pais } : {}),
    ...(i.invitacion ? { invitacion: i.invitacion } : {}),
  });
}

/** URL relativa que arranca el acceso con Google (navegación completa). */
export function urlGoogleInicio(o?: {
  siguiente?: string;
  idioma?: string;
  /** País de la pantalla de acceso (§21.4); el backend lo lleva en el `state`. */
  pais?: string | null;
  /** El token de `/start?invite=` (030-lite). */
  invitacion?: string | null;
}): string {
  const query = new URLSearchParams();
  if (o?.siguiente) query.set("siguiente", o.siguiente);
  if (o?.idioma) query.set("idioma", o.idioma);
  if (o?.pais) query.set("pais", o.pais);
  if (o?.invitacion) query.set("invitacion", o.invitacion);
  const qs = query.toString();
  return qs ? `/api/auth/google/start?${qs}` : "/api/auth/google/start";
}

/** Valores de `?error=` con los que vuelve el acceso con Google (§8.3). */
export type ErrorGoogle =
  | "google_cancelado"
  | "google_fallo"
  | "google_2fa"
  | "cuenta_suspendida"
  // Feature 026, 2.ª tanda: la cuenta de Google es de un dominio desechable.
  | "correo_temporal"
  | "alta_restringida"
  // 030-lite: el enlace de invitación caducó o ya se usó, o es para otro correo.
  | "invitacion_invalida"
  | "invitacion_otro_correo";

// Los textos legales viven en la web de Zirvo (2026-10-02); las variables los cambian por entorno.
export const URL_TERMINOS: string | null = process.env.NEXT_PUBLIC_URL_TERMINOS || "https://zirvo.ai/terms.html";
export const URL_PRIVACIDAD: string | null =
  process.env.NEXT_PUBLIC_URL_PRIVACIDAD || "https://zirvo.ai/privacy.html";
